import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import type { ShiftStreamEvent } from 'shared-models';
import { useShiftAuth } from './ShiftAuthProvider';

type StreamEvent = ShiftStreamEvent | { type: 'sse.resync' };
type Listener = (event: StreamEvent) => void;
const ShiftEventsContext = createContext<{ subscribe: (listener: Listener) => () => void; isReady: boolean } | null>(null);
const eventTypes = new Set(['employeePriority.upserted', 'employeeEligibility.upserted', 'employeeTipSettings.upserted', 'employeeScheduling.invalidated', 'staffingDemand.upserted', 'staffingDemand.invalidated', 'home.assignmentsChanged', 'home.announcementChanged', 'vacation.upserted', 'vacation.removed', 'attendance.upserted', 'attendance.removed', 'cost.upserted', 'cost.removed', 'costValue.upserted', 'costValue.removed', 'tip.upserted', 'tip.removed', 'tip.sourceChanged', 'tip.policyChanged', 'sectionTipMinimum.upserted', 'shift.tipClassificationChanged', 'sse.resync']);
eventTypes.add('schedule.publicationChanged');
eventTypes.add('governance.changed');
eventTypes.add('availability.lockChanged');
eventTypes.add('employeeLimits.upserted');
eventTypes.add('weekdayPriority.upserted');
eventTypes.add('schedule.draftChanged');
eventTypes.add('employeeMetadata.upserted');
eventTypes.add('account.upserted');
eventTypes.add('account.selfChanged');
eventTypes.add('notification.upserted');

export function ShiftEventsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user, applyAccountAccess } = useShiftAuth();
  const applyAccess = useRef(applyAccountAccess);
  applyAccess.current = applyAccountAccess;
  const listeners = useRef(new Set<Listener>());
  const [isReady, setIsReady] = useState(false);
  const [subscribe] = useState(() => (listener: Listener) => {
    listeners.current.add(listener);
    return () => { listeners.current.delete(listener); };
  });
  useEffect(() => {
    setIsReady(false);
    if (!isAuthenticated || !user) return;
    const source = new EventSource(`${process.env.REACT_APP_BACKEND_LOCATION ?? '/__shift_api__'}/shift/events`, { withCredentials: true });
    let recoveringInitialFailure = false;
    source.onerror = () => {
      recoveringInitialFailure = true;
      setIsReady(true);
    };
    source.onmessage = (message) => {
      let event: StreamEvent | { type: 'sse.head' };
      try { event = JSON.parse(message.data); } catch { return; }
      if (event?.type === 'sse.head') {
        setIsReady(true);
        if (recoveringInitialFailure) {
          recoveringInitialFailure = false;
          for (const listener of listeners.current) listener({ type: 'sse.resync' });
        }
        return;
      }
      if (!event || !eventTypes.has(event.type)) return;
      if (event.type==='account.selfChanged') applyAccess.current?.(event.access);
      for (const listener of listeners.current) listener(event);
    };
    return () => source.close();
  }, [isAuthenticated, user?.id, user?.tenantPk]);
  return <ShiftEventsContext.Provider value={{ subscribe, isReady }}>{children}</ShiftEventsContext.Provider>;
}

export function useShiftEvents(listener: Listener) {
  const context = useContext(ShiftEventsContext);
  const subscribe = context?.subscribe;
  const handler = useRef(listener);
  handler.current = listener;
  useEffect(() => subscribe?.((event) => handler.current(event)), [subscribe]);
  return context?.isReady ?? true;
}